// src/Dashboard/pages/admin/Attendance/components/AttendanceGroupPicker.jsx
import React, { useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaLayerGroup,
  FaSearch,
  FaUsers,
} from "react-icons/fa";

import { getGroupName, getGroupSubtitle } from "../utils/attendanceUtils";

function getColorClass(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-white text-slate-900 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-white dark:ring-slate-700";
}

export default function AttendanceGroupPicker({
  loading,
  groups = [],
  filters,
  setFilter,
}) {
  const [query, setQuery] = useState("");

  const filteredGroups = useMemo(() => {
    const search = query.trim().toLowerCase();

    return groups.filter((group) => {
      if (!search) return true;

      return [
        group?.nom,
        group?.horaire1,
        group?.horaire2,
        group?.enseignant1,
        group?.enseignant2,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [groups, query]);

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
      <div className="mx-auto max-w-4xl text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300">
          <FaLayerGroup className="text-2xl" />
        </div>

        <p className="mt-4 text-sm font-bold uppercase tracking-[0.24em] text-orange-500">
          Démarrer l’appel
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
          Choisir le groupe du cours
        </h2>

        <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
          L’appel est enregistré par groupe et par date. Un même adhérent peut
          donc être appelé dans plusieurs groupes le même jour.
        </p>
      </div>

      <div className="mx-auto mt-6 grid max-w-4xl gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
        <label className="block">
          <span className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <FaCalendarAlt />
            Date de l’appel
          </span>

          <input
            type="date"
            value={filters.selectedDate}
            disabled={loading}
            onChange={(event) => setFilter("selectedDate", event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Rechercher un groupe
          </span>

          <span className="relative block">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nom du groupe, horaire, enseignant..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
            />
          </span>
        </label>
      </div>

      <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Groupes disponibles
            </h3>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Touchez un groupe pour lancer l’appel.
            </p>
          </div>

          <span className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
            <FaUsers />
            {filteredGroups.length}
          </span>
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-28 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            Aucun groupe ne correspond à votre recherche.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {filteredGroups.map((group) => {
              const subtitle = getGroupSubtitle(group);

              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setFilter("selectedGroupId", String(group.id))}
                  className={`group flex min-h-[112px] items-center gap-4 rounded-3xl px-4 py-4 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg active:scale-[0.98] ${getColorClass(
                    group
                  )}`}
                >
                  <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-slate-950 text-white shadow-sm transition group-hover:scale-105 dark:bg-white dark:text-slate-950">
                    <FaLayerGroup className="text-xl" />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-bold">
                      {getGroupName(group)}
                    </span>

                    {subtitle && (
                      <span className="mt-1 flex items-center gap-1 truncate text-xs opacity-70">
                        <FaClock />
                        {subtitle}
                      </span>
                    )}

                    <span className="mt-3 inline-flex rounded-full bg-white/75 px-3 py-1 text-[11px] font-bold text-slate-700 ring-1 ring-black/5 dark:bg-slate-950/75 dark:text-slate-200 dark:ring-white/10">
                      Commencer l’appel
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}